import React, { useState, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  easeInOut,
  easeOut,
  easeIn,
} from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: "Wellness Begins with the Mind",
      text: "Our thoughts shape how we experience life. A restless mind obscures perception and distorts judgement, while a calm, positive mindset eases stress and broadens perspective. As we nurture clarity of mind and emotional balance, the body often follows with renewed energy and vitality. A balanced mind is the key to inner transformation and lasting wellness.  ",
      cta: "Learn More ",
      showButtons: false,
    },
    {
      title: "Deep Relaxation with Yoga Nidra",
      text: "Yoga Nidra is a restorative practice that promotes profound physical, emotional and mental relaxation. As the body rests without movement, the nervous system calms and mental noise fades. This stillness allows buried tension and emotions to release gently. In Yoga Nidra, stillness is not just a lack of movement but a deeply fertile ground where the body restores itself, the mind finds clarity, and inner harmony prevails. ",
      cta: "Begin Journey",
      showButtons: false,
    },
    {
      title: "How Cittasya® Works",
      text: "Download the app, select a Yoga Nidra session, and begin your journey. Record your feedback to track progress and support your wellness goals. Repeat and practice daily to move through stages to deepen your experience. With consistency, you'll build a life-changing habit and unlock the healing power of rest and awareness. Healing doesn't come from force but from surrender and faith in the practice.  ",
      cta: "Download App",
      showButtons: true,
    },
  ];

  const backgrounds = ["/main.png", "/mainbg2.jpg", "/mainbg3.jpg"];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setBackgroundIndex((prev) => (prev + 1) % backgrounds.length);
    }, 20000);
    return () => clearInterval(timer);
  }, []);

  const handleCTAClick = (slideIndex: number) => {
    if (slideIndex === 0) {
      // "Learn More" button - navigate to /about
      navigate('/about');
    } else if (slideIndex === 1) {
      // "Begin Journey" button - navigate to /contact
      navigate('/contact');
    }
  };

  const backgroundVariants = {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: easeInOut,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: easeInOut,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: easeOut,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: easeIn,
      },
    },
  };

  return (
    <section
      id="home"
      className="relative min-h-[calc(100vh-4rem)] sm:min-h-[70vh] md:min-h-[80vh] flex flex-col justify-between overflow-hidden pt-16 sm:pt-20"
    >
      {/* Background Images with Smooth Transitions */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={backgroundIndex}
            variants={backgroundVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${backgrounds[backgroundIndex]})`,
              }}
            />
            {/* Enhanced overlay for better text readability with darker blackish tone */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Floating decorative elements */}
      <motion.div
        className="absolute right-6 sm:right-10 top-1/4 w-20 sm:w-24 md:w-32 h-20 sm:h-24 md:h-32 rounded-full bg-soft-saffron/20 blur-2xl hidden sm:block"
        animate={{
          y: [0, -20, 0],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute left-8 sm:left-16 bottom-1/4 w-16 sm:w-20 md:w-24 h-16 sm:h-20 md:h-24 rounded-full bg-copper-brown/20 blur-xl hidden sm:block"
        animate={{
          y: [0, 15, 0],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        <div className="container mx-auto px-4 sm:px-6 py-16">
          <div className="max-w-6xl mx-auto text-center">
            {/* Slide Content with Smooth Transitions */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-6"
              >
                <motion.h1
                  className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-cloud-white mb-6 leading-tight drop-shadow-2xl"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: easeOut }}
                >
                  {slides[currentSlide].title === "How Cittasya® Works" ? (
                    <>
                      How <span style={{ fontWeight: "normal" }}>Cittasya</span>
                      ® Works
                    </>
                  ) : (
                    slides[currentSlide].title
                  )}
                </motion.h1>

                <motion.p
                  className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-cloud-white mb-8 leading-relaxed max-w-6xl mx-auto font-light drop-shadow-lg px-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: easeOut }}
                >
                  {slides[currentSlide].text}
                </motion.p>

                <motion.div
                  className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6, ease: easeOut }}
                >
                  {!slides[currentSlide].showButtons && (
                    <Button
                      size="lg"
                      className="bg-forest-green hover:bg-copper-brown text-cloud-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl border-2 border-cloud-white/20 w-full sm:w-auto"
                      onClick={() => handleCTAClick(currentSlide)}
                    >
                      {slides[currentSlide].cta}
                    </Button>
                  )}

                  {slides[currentSlide].showButtons && (
                    <motion.div
                      className="flex flex-col sm:flex-row gap-4 items-center w-full sm:w-auto"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                    >
                      {/* App Store Button */}
                      <button className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl w-full sm:w-auto">
                        <div className="bg-black text-white px-4 sm:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 rounded-xl border border-gray-600 hover:border-gray-400 transition-all duration-300 justify-center sm:justify-start w-full">
                          <img
                            src="/apple.svg"
                            alt="Apple Logo"
                            className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12"
                          />
                          <div className="text-left">
                            <div className="text-xs text-gray-300">
                              Download on the
                            </div>
                            <div className="text-base sm:text-lg font-semibold leading-tight">
                              App Store
                            </div>
                          </div>
                        </div>
                      </button>

                      {/* Google Play Button */}
                      <button className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl w-full sm:w-auto">
                        <div className="bg-black text-white px-4 sm:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 rounded-xl border border-gray-600 hover:border-gray-400 transition-all duration-300 justify-center sm:justify-start w-full">
                          <img
                            src="/google-play.svg"
                            alt="Google Play Logo"
                            className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12"
                          />
                          <div className="text-left">
                            <div className="text-xs text-gray-300">
                              GET IT ON
                            </div>
                            <div className="text-base sm:text-lg font-semibold leading-tight">
                              Google Play
                            </div>
                          </div>
                        </div>
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Dots positioned at bottom */}
      <div className="relative z-10 pb-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              className="flex justify-center space-x-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
            >
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index);
                    setBackgroundIndex(index);
                  }}
                  className={`relative group transition-all duration-500 ${
                    index === currentSlide ? "scale-110" : "hover:scale-110"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full transition-all duration-500 ${
                      index === currentSlide
                        ? "shadow-lg"
                        : "backdrop-blur-sm border-2 border-cloud-white/40 hover:bg-copper-brown/60"
                    }`}
                    style={{
                      backgroundColor:
                        index === currentSlide
                          ? "#3FBA50"
                          : "rgba(255, 255, 255, 0.6)",
                      boxShadow:
                        index === currentSlide
                          ? "0 10px 15px -3px rgba(63, 186, 80, 0.5)"
                          : "none",
                    }}
                  />
                  {index === currentSlide && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute inset-0 rounded-full border-2"
                      style={{ borderColor: "rgba(63, 186, 80, 0.5)" }}
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
